import { useEffect, useRef, useState } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { openTerminalWS, laneAction, laneSend } from "./api";

export function TerminalPanel({ lane, onClose }: { lane: string; onClose: () => void }) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [writable, setWritable] = useState(false);
  const [draft, setDraft] = useState("");
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!hostRef.current) return;
    const term = new Terminal({
      theme: { background: "#0b0d10", foreground: "#dbe2ec", cursor: "#7cc9ff" },
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: 12,
      convertEol: true,
      disableStdin: true,
      scrollback: 2000,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(hostRef.current);
    fit.fit();
    termRef.current = term;
    fitRef.current = fit;

    const ws = openTerminalWS(lane);
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onmessage = (ev) => term.write(typeof ev.data === "string" ? ev.data : "");
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);

    const onResize = () => fit.fit();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      try { ws.close(); } catch { /* ignore */ }
      term.dispose();
      termRef.current = null;
      fitRef.current = null;
      wsRef.current = null;
    };
  }, [lane]);

  return (
    <div className="fixed top-12 right-0 bottom-0 w-[640px] bg-panel border-l border-edge flex flex-col z-40">
      <header className="flex items-center gap-3 px-4 h-10 border-b border-edge shrink-0">
        <span className="font-mono text-sm">{lane}</span>
        <span className={`text-xs ${connected ? "text-good" : "text-mist"}`}>
          {connected ? "● live" : "○ connecting"}
        </span>
        <div className="ml-auto flex items-center gap-2 text-xs">
          <button
            onClick={() => laneAction(lane, "embody")}
            className="border border-edge rounded px-2 py-0.5 hover:border-mist"
          >embody</button>
          <button
            onClick={() => laneAction(lane, "disembody")}
            className="border border-edge rounded px-2 py-0.5 hover:border-mist"
          >disembody</button>
          <label className="flex items-center gap-1 text-mist">
            <input type="checkbox" checked={writable} onChange={(e) => setWritable(e.target.checked)} />
            writable
          </label>
          <button
            onClick={onClose}
            className="border border-edge rounded px-2 py-0.5 hover:border-mist"
            aria-label="Close"
          >✕</button>
        </div>
      </header>
      <div ref={hostRef} className="flex-1 min-h-0 px-2 py-2 overflow-hidden" />
      {writable && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!draft.trim()) return;
            laneSend(lane, draft);
            setDraft("");
          }}
          className="border-t border-edge p-2 flex gap-2"
        >
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`send to ${lane}…`}
            className="flex-1 bg-ink border border-edge rounded px-2 py-1 text-sm font-mono"
          />
          <button className="border border-edge rounded px-3 py-1 text-sm hover:border-mist">send</button>
        </form>
      )}
    </div>
  );
}
